'use client';

import type { ReactElement } from 'react';
import { Fragment } from 'react';
import type { Control } from 'react-hook-form';
import type { Contact } from '@/lib/services/contacts/types';
import { NameSection } from './NameSection';
import { ContactInfoSection } from './ContactInfoSection';
import { AddressSection } from './AddressSection';
import { RoleSection } from './RoleSection';
import { NotesSection } from './NotesSection';
import { BankAccountSection } from './BankAccountSection';
import type { ContactType } from './ContactTypeSelector';
import {
  OtherAddressesSubsection,
  OtherNamesSubsection
} from '@/components/identity-variants';

export interface ContactFormProps {
  control: Control<Partial<Contact>>;
  /**
   * Snapshot of the contact used for read-mode display and for initialising
   * collapsible "+ Add …" toggles. NOT a live reactive view — sections read
   * live edit-mode values via Controller.
   */
  values: Partial<Contact>;
  mode: 'read' | 'edit';
  clickToCopy?: boolean;
  customAction?: React.ReactNode;
  contactType: ContactType;
  onTypeChange: (type: ContactType) => void;
  hideBankAccount?: boolean;
  showRoles?: boolean;
  showMatterNotes?: boolean;
  showFileNote?: boolean;
  /**
   * Whether the matter is estate-administration. Passed to `RoleSection` so it
   * renders the estate controls (LPR / Beneficiary) vs the non-estate
   * client-only Role select (LF-5788). Defaults to `true`.
   */
  isEstate?: boolean;
  /**
   * The matter this contact is being edited on, when there is one.
   *
   * Only used to surface the contact's PMS binding + its explicit unbind action, which is
   * meaningful solely per `(contact, matter)`. Omitted on the surfaces that reuse this form with no
   * matter behind it — the global contacts list, and the AI-review cards, which stage *proposed*
   * contacts that are not filed anywhere yet.
   */
  matterId?: number;
  headerContent?: React.ReactNode;
  /**
   * Render the "Other names" / "Other addresses" subsections for this contact
   * (LF-5679). Opt-in, and only on a genuine edit surface — the AI-review cards
   * reuse this form to stage *proposed* contacts, where a live child-row editor
   * writing straight to the database would be wrong.
   */
  showIdentityVariants?: boolean;
}

export function ContactForm({
  control,
  values,
  mode,
  clickToCopy = false,
  customAction,
  contactType,
  onTypeChange,
  hideBankAccount = false,
  showRoles = true,
  showMatterNotes = false,
  showFileNote = false,
  isEstate = true,
  matterId,
  headerContent,
  showIdentityVariants = false
}: ContactFormProps): ReactElement {
  return (
    <Fragment>
      {headerContent}
      {headerContent ? (
        <div className="flex items-center gap-3">
          <div className="bg-border h-px flex-1" />
          <span className="text-muted-foreground text-xs font-medium">OR</span>
          <div className="bg-border h-px flex-1" />
        </div>
      ) : null}

      <NameSection
        control={control}
        mode={mode}
        values={values}
        clickToCopy={false}
        contactType={contactType}
        onTypeChange={onTypeChange}
      />

      {/*
       * Other names supplement the base name above — never replace it
       * (LF-5566 PROV-3) — so they fold under the name fields as a subsection
       * (LF-5714). Edit mode only: the read-mode card is a compact list row,
       * and these are live child-row mutations rather than part of this form's
       * submit.
       */}
      {showIdentityVariants && mode === 'edit' && (
        <OtherNamesSubsection ownerType="contact" ownerId={values.id} />
      )}

      {showRoles && (
        <RoleSection
          control={control}
          mode={mode}
          values={values}
          clickToCopy={false}
          isEstate={isEstate}
          matterId={matterId}
        />
      )}

      <ContactInfoSection
        control={control}
        mode={mode}
        values={values}
        clickToCopy={clickToCopy}
        customAction={customAction}
      />

      <AddressSection
        control={control}
        mode={mode}
        values={values}
        clickToCopy={false}
      />

      {/* Previous / alternative addresses, folded under the address fields. */}
      {showIdentityVariants && mode === 'edit' && (
        <OtherAddressesSubsection ownerType="contact" ownerId={values.id} />
      )}

      {!hideBankAccount && (
        <BankAccountSection
          control={control}
          mode={mode}
          values={values}
          clickToCopy={false}
          showFileNote={showFileNote}
        />
      )}

      <NotesSection
        control={control}
        mode={mode}
        values={values}
        clickToCopy={false}
        showMatterNotes={showMatterNotes}
      />
    </Fragment>
  );
}
